import { useFrame } from '$shared/utils/frame/hooks/use-frame';
import { useKyoceraRouter } from './../useKyoceraRouter/useKyoceraRouter';
import { useNotification } from '$features/notifications/hooks';
import { cancellableFetch, useTranslation } from '$shared/utils';
import { fetcher } from '$shared/utils/request/utils/fetcher';
import { BASKET_API_URL } from '$shared/utils/search/service/endpoints';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TranslationKey } from '~/lib/data-contract';
import { useAuthentication } from '../useAuthentication';
import { BasketDto, ServiceDto } from '~/lib/data-contract/auto-generated/api-basket-service-model';
import { createQuoteQueryKey } from '$features/checkout/hooks/use-create-quote';
import {pushEcommerceEvent} from "$services/piwik/ecommerce-tracking.service";
import {useProductCompare} from "$features/product-compare/product-compare.store";

const buildEcommerceFromBasketLine = (
    line: any,
    deltaQuantity: number
) => {
    if (!line || deltaQuantity === 0) return null;

    const absQty = Math.abs(deltaQuantity);

    const itemId =
        (line as any).sanitizedItemNumber ??
        (line as any).originalItemNumber ??
        (line as any).itemNumber ??
        '';

    return {
        items: [
            {
                item_id: itemId,
                item_name: itemId,
                quantity: absQty,
            },
        ],
    };
};

const basketSimpleQueryKey = 'simpleBasketQuery';
const basketCalculatedQueryKey = 'calculatedBasketQuery';

type AddToBasketArgs = {
    sanitizedItemNumber: string;
    quantity: number;
    lineId?: string;
    services?: ServiceDto[];
};

type AddOriginalItemNumberToBasketArgs = {
    itemNumber: string;
    quantity: number;
};

/**
 * Hook for getting a simple version of the basket.
 * More performant than calculated basket, do use this if the data in here is sufficient.
 */
export const useSimpleBasket = () => {
    const { isAuthenticated, userFound } = useAuthentication();
    const { push } = useNotification();
    const queryClient = useQueryClient();

    const { data: simpleBasket, isLoading } = useQuery<BasketDto>(
        [basketSimpleQueryKey],
        ({ signal }) =>
            cancellableFetch({ signal, url: `${BASKET_API_URL}/basket/getSimpleBasket` }),
        {
            // Only request when the user is authenticated
            enabled: Boolean(isAuthenticated && userFound),
            // Set basket to stale after  5 minutes
            staleTime: 5 * 60 * 1000,
            retry: (failureCount, error) => {
                if (error instanceof Response && error?.status === 404) {
                    queryClient.removeQueries([basketSimpleQueryKey], { exact: true });

                    push({
                        severity: 'error',
                        text: 'basket.notification-basket-get-error' as TranslationKey,
                    });

                    return false;
                }

                return failureCount < 3;
            },
            onError: async (err) => {
                console.error(err);
                push({
                    text: 'basket.notification-basket-get-error' as TranslationKey,
                });
            },
        }
    );

    const basketCount = simpleBasket?.lines?.reduce((acc, cur) => acc + (cur?.quantity || 0), 0);

    return { basketCount, simpleBasket, isLoading };
};

/**
 * Hook for getting calculated basket (with prices).
 * Intended for basketpage, as this is quite performance heavy.
 * Utilise `useSimpleBasket` for general usage such as basket count badge etc.
 */
// export const useCalculatedBasket = () => {
//     const { isAuthenticated, userFound } = useAuthentication();
//     const { push } = useNotification();
//     const queryClient = useQueryClient();
//     const canRequestCalculatedBasketReady =
//         isAuthenticated !== undefined && userFound !== undefined;
//     const canRequestCalculatedBasket = Boolean(isAuthenticated && userFound);
//     const serveEmptyResult = canRequestCalculatedBasketReady && !canRequestCalculatedBasket;

//     const { data: calculatedBasket, isLoading, isRefetching } = useQuery<CalculatedBasketViewModel>(
//         [basketCalculatedQueryKey],
//         ({ signal }) =>
//             cancellableFetch({ signal, url: `${BASKET_API_URL}/basket/getCalculatedBasket` }),
//         {
//             // Only request when the user is authenticated
//             enabled: canRequestCalculatedBasket,
//             // Set basket to stale after  5 minutes
//             staleTime: 5 * 60 * 1000,
//             retry: (failureCount, error) => {
//                 if (error instanceof Response && error?.status === 404) {
//                     queryClient.removeQueries([basketCalculatedQueryKey], { exact: true });

//                     push({
//                         severity: 'error',
//                         text: 'basket.notification-basket-get-error' as TranslationKey,
//                     });

//                     return false;
//                 }

//                 return failureCount < 3;
//             },
//             onError: async (err) => {
//                 console.error(err);
//                 push({
//                     text: 'basket.notification-basket-get-error' as TranslationKey,
//                 });
//             },
//         }
//     );

//     const basketCount = calculatedBasket?.basketLines?.reduce(
//         (acc, cur) => acc + (cur?.quantity || 0),
//         0
//     );

//     return {
//         basketCount,
//         calculatedBasket: serveEmptyResult ? {} : { ...calculatedBasket },
//         isLoading: serveEmptyResult ? false : isLoading,
//         isRefetching: serveEmptyResult ? false : isRefetching,
//     };
// };

/**
 * Hook for upserting basket.
 * Upsert is absolute meaning the quantity provided will stand as the new value regardless of previous quantity.
 * BE handles merge logic if an added product is already present somewhere else in the basket.
 * Quantity 0 removes the line, but requires a lineId.
 */
export const useBasketUpsert = (giveNotifications = true) => {
    const { isAuthenticated, userFound } = useAuthentication();
    const queryClient = useQueryClient();
    const { push } = useNotification();
    const { push: routerPush } = useKyoceraRouter();
    const { basketPage } = useFrame();
    const { translate } = useTranslation();
    const closeCompareForNavigation = useProductCompare((state) => state.closeCompareForNavigation);

    const {
        isLoading,
        mutateAsync: upsertBasket,
        status,
    } = useMutation<
        BasketDto,
        Response,
        AddToBasketArgs,
        { previousBasket?: BasketDto }
    >(
        async (itemToAdd) => {
            if (itemToAdd.quantity === 0 && !itemToAdd.lineId) {
                throw new Error('Cannot delete line without a lineId');
            }
            if (!(isAuthenticated && userFound)) {
                throw new Error('Cannot upsert basket without being authenticated');
            }

            const request = fetcher<BasketDto>(
                `${BASKET_API_URL}/basket/upsert?sanitizedItemNumber=${
                    itemToAdd.sanitizedItemNumber
                }&quantity=${itemToAdd.quantity}${
                    itemToAdd.lineId ? `&lineId=${itemToAdd.lineId}` : ''
                }`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(itemToAdd.services || []),
                }
            );

            const response = await request();

            return response;
        },
        {
            onMutate: async () => {
                const previousBasket = queryClient.getQueryData<BasketDto>([basketSimpleQueryKey]);
                return { previousBasket };
            },
            onSuccess: (responseSuccess, variables, context) => {
                if (responseSuccess) {
                    queryClient.setQueryData([basketSimpleQueryKey], responseSuccess);
                    queryClient.invalidateQueries([createQuoteQueryKey]);

                    if (giveNotifications) {
                        push({
                            severity: 'success',
                            text: translate('basket.notification-basket-update'),
                            onClick: () => {
                                closeCompareForNavigation();
                                basketPage?.url && routerPush(basketPage?.url)
                            },
                        });
                    }

                    try {
                        const previousBasket = context?.previousBasket;
                        const prevLine = previousBasket?.lines?.find(
                            (l: any) =>
                                l.sanitizedItemNumber === variables.sanitizedItemNumber
                        );
                        const newLine = responseSuccess?.lines?.find(
                            (l: any) =>
                                l.sanitizedItemNumber === variables.sanitizedItemNumber
                        );

                        const prevQty = prevLine?.quantity ?? 0;
                        const newQty = newLine?.quantity ?? 0;

                        const effectiveNewQty = newLine ? newQty : 0;
                        const delta = effectiveNewQty - prevQty;

                        if (delta === 0) {
                            return;
                        }

                        const lineForMeta = newLine || prevLine;
                        const ecommercePayload = buildEcommerceFromBasketLine(
                            lineForMeta,
                            delta
                        );

                        if (!ecommercePayload) return;

                        const eventName = delta > 0 ? 'add_to_cart' : 'remove_from_cart';
                        pushEcommerceEvent(eventName, ecommercePayload);
                    } catch (e) {
                        console.error('[Ecommerce tracking] basket upsert error', e);
                    }
                } else {
                    push({
                        text: translate('basket.notification-basket-error'),
                    });
                }
            },
            onError: (err) => {
                console.error(err);
                push({
                    text: translate('basket.notification-basket-error'),
                });
            },
        }
    );

    return {
        isLoading,
        upsertBasket,
        status,
    };
};

export const useBasketUpsertOriginalItemNumber = (giveNotifications = true) => {
    const { isAuthenticated, userFound } = useAuthentication();
    const queryClient = useQueryClient();
    const { push } = useNotification();
    const { push: routerPush } = useKyoceraRouter();
    const { basketPage } = useFrame();
    const { translate } = useTranslation();

    const {
        isLoading,
        mutateAsync: upsertOriginalItemNumber,
        status,
    } = useMutation<
        BasketDto,
        Response,
        AddOriginalItemNumberToBasketArgs,
        { previousBasket?: BasketDto }
    >(
        async (itemToAdd) => {
            if (itemToAdd.quantity < 0) {
                throw new Error('Invalid Quantity');
            }
            if (!(isAuthenticated && userFound)) {
                throw new Error('Cannot upsert basket without being authenticated');
            }

            const request = fetcher<BasketDto>(
                `${BASKET_API_URL}/basket/UpsertOriginalItemNumber/oitem?itemNumber=${encodeURIComponent(
                    itemToAdd.itemNumber
                )}&quantity=${itemToAdd.quantity}`,
                {
                    method: 'POST',
                }
            );

            const response = await request();

            return response;
        },
        {
            onMutate: async () => {
                const previousBasket = queryClient.getQueryData<BasketDto>([basketSimpleQueryKey]);
                return { previousBasket };
            },
            onSuccess: (responseSuccess, variables, context) => {
                if (responseSuccess) {
                    if (giveNotifications) {
                        push({
                            severity: 'success',
                            text: translate('basket.notification-basket-update'),
                            onClick: () => {
                                if (basketPage?.url) {
                                    routerPush(basketPage.url);
                                }
                            },
                        });
                    }

                    queryClient.setQueryData([basketSimpleQueryKey], responseSuccess);
                    queryClient.invalidateQueries([createQuoteQueryKey]);

                    try {
                        const previousBasket = context?.previousBasket;
                        const prevLine = previousBasket?.lines?.find(
                            (l: any) =>
                                l.originalItemNumber === variables.itemNumber ||
                                l.sanitizedItemNumber === variables.itemNumber
                        );
                        const newLine = responseSuccess?.lines?.find(
                            (l: any) =>
                                l.originalItemNumber === variables.itemNumber ||
                                l.sanitizedItemNumber === variables.itemNumber
                        );

                        const prevQty = prevLine?.quantity ?? 0;
                        const newQty = newLine?.quantity ?? 0;

                        const effectiveNewQty = newLine ? newQty : 0;
                        const delta = effectiveNewQty - prevQty;

                        if (delta === 0) {
                            return;
                        }

                        const lineForMeta = newLine || prevLine;
                        const ecommercePayload = buildEcommerceFromBasketLine(
                            lineForMeta,
                            delta
                        );

                        if (!ecommercePayload) return;

                        const eventName = delta > 0 ? 'add_to_cart' : 'remove_from_cart';
                        pushEcommerceEvent(eventName, ecommercePayload);
                    } catch (e) {
                        console.error('[Ecommerce tracking] basket upsert (original item) error', e);
                    }
                } else {
                    push({
                        text: translate('basket.notification-basket-error'),
                    });
                }
            },
            onError: (err) => {
                console.error(err);
                push({
                    text: translate('basket.notification-basket-error'),
                });
            },
        }
    );

    return {
        isLoading,
        upsertOriginalItemNumber,
        status,
    };
};

/**
 * util for clearing entire basket states.
 */
export const useResetBasket = () => {
    const queryClient = useQueryClient();

    const resetAllBasketState = () => {
        queryClient.resetQueries([basketCalculatedQueryKey], {
            exact: true,
        });
        queryClient.resetQueries([basketSimpleQueryKey], {
            exact: true,
        });
    };

    return { resetAllBasketState };
};

export const useBasketClear = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: clearBasket, isLoading, status } = useMutation<void, Response>(
        async () => {
            const request = fetcher(`${BASKET_API_URL}/basket/clearAll`, {
                method: 'POST',
            });
            await request();
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([basketSimpleQueryKey]);
                queryClient.invalidateQueries([basketCalculatedQueryKey]);

            },
            onError: (err) => {
                console.error(err);
            },
        }
    );

    return {
        clearBasket,
        isLoading,
        status,
    };
};
